import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ServiceIcon from '../components/ServiceIcon';
import { Button } from '../components/ui/button';
import { services, blogPosts } from '../data/mock';
import SEOHead from '../components/seo/SEOHead';
import { OrganizationSchema, BreadcrumbSchema } from '../components/seo/StructuredData';
import { PAGE_SEO } from '../data/routeSeo';

const Services = () => {
  const recentPosts = blogPosts.slice(0, 3);

  return (
    <Layout>
      <SEOHead
        {...PAGE_SEO['/services']}
      />
      <OrganizationSchema />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Services', path: '/services' }
      ]} />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
            Our Services
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            AI Systems Built for{' '}
            <span className="text-emerald-400">Nigerian Business</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            From shortlet apartments in Lagos to clinics in Abuja and law firms in
            Port Harcourt — twelve done-for-you services that answer every enquiry,
            book every appointment and follow up until the deal is done.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => {
              const isEven = index % 2 === 0;

              return (
                <div
                  key={service.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${
                    isEven ? '' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Image */}
                  <div className={`relative ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                    <div
                      className="absolute inset-0 rounded-3xl blur-2xl opacity-30"
                      style={{ backgroundColor: service.color }}
                    />
                    <img
                      src={service.image}
                      alt={service.title}
                      className="relative rounded-3xl w-full aspect-[4/3] object-cover shadow-xl"
                    />
                    <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur rounded-xl p-4">
                      <p className="text-sm text-gray-500">Starting from</p>
                      <p className="text-xl font-bold text-gray-900">{service.pricing}</p>
                      {service.setupFee && (
                        <p className="text-xs text-gray-500 mt-1">{service.setupFee}</p>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                      style={{ backgroundColor: `${service.color}15` }}
                    >
                      <div style={{ color: service.color }}>
                        <ServiceIcon icon={service.icon} className="w-10 h-10" />
                      </div>
                    </div>

                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Features Preview */}
                    <div className="grid grid-cols-2 gap-3 mb-8">
                      {service.features.slice(0, 4).map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-gray-700">
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: service.color }}
                          />
                          <span className="text-sm">{feature.split(' ').slice(0, 4).join(' ')}</span>
                        </div>
                      ))}
                    </div>

                    <Link to={`/services/${service.id}`}>
                      <Button
                        size="lg"
                        className="font-semibold px-8 rounded-xl text-white"
                        style={{ backgroundColor: service.color }}
                      >
                        Learn More
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Learn More on Our Blog</h2>
            <Link to="/blog" className="text-emerald-600 font-medium hover:text-emerald-700 flex items-center gap-1">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {recentPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="text-emerald-600 text-sm font-medium">{post.category}</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-2 group-hover:text-emerald-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Not Sure Which Service Is Right For You?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Book a free 15-minute call. We'll audit how enquiries reach you today and
            recommend the fastest win — no obligation.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-10 py-6 text-lg rounded-xl"
            >
              Get Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
